import React, { useState } from 'react';
import { Button, Empty, Input, Select } from 'antd';
import { CheckOutlined, CloseOutlined, FilterOutlined, MessageOutlined, ReloadOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import PageScaffold from '../../components/PageScaffold';
import ContactDrawer from '../contacts/components/ContactDrawer';
import useConversationsPageController from './controllers/useConversationsPageController';
import ConversationList from './components/ConversationList';
import ConversationThread from './components/ConversationThread';
import ConversationContactPanel from './components/ConversationContactPanel';
import {
  CONVERSATIONS_PERIOD_OPTIONS,
  CONVERSATIONS_READ_STATE_OPTIONS,
  CONVERSATIONS_SORT_OPTIONS,
  CONVERSATIONS_TABS,
} from './types/ConversationDisplay';
import '../contacts/ContactsPage.css';
import './ConversationsPage.css';

export const CONVERSATIONS_PAGE_PATH = '/conversations';
export const CONVERSATIONS_PAGE_TITLE = 'Conversations';
export const CONVERSATIONS_MENU_TITLE = 'Activité';

const ConversationsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Conversation opened from the "Conversation" button of a contact
  const [initialTarget] = useState(() => {
    const subscriptionId = searchParams.get('subscription');
    const phone = searchParams.get('phone');
    return subscriptionId && phone ? { subscriptionId, phone } : null;
  });
  const { state, actions } = useConversationsPageController(initialTarget);
  const { filters, selectedConversation } = state;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  return (
    <PageScaffold title={CONVERSATIONS_PAGE_TITLE} hideHeader>
      <div className="conversations">
        <div className="conversations__inbox">
          <div className="conversations__inbox-head">
            <h1 className="conversations__title">
              {CONVERSATIONS_PAGE_TITLE}
              {!state.isLoading && <span className="conversations__count">{state.totalCount}</span>}
            </h1>
            <Button type="text" icon={<ReloadOutlined />} onClick={actions.refresh} aria-label="Rafraîchir" />
          </div>

          <Input
            prefix={<SearchOutlined />}
            placeholder="Rechercher…"
            allowClear
            value={filters.search}
            onChange={(e) => actions.setSearch(e.target.value)}
          />

          <div className="conversations__tabs" role="tablist">
            {CONVERSATIONS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={filters.tab === tab.value}
                className={classNames('conversations__tab', { 'conversations__tab--active': filters.tab === tab.value })}
                onClick={() => actions.setTab(tab.value)}
              >
                {tab.label}
                {state.unreadByTab[tab.value] > 0 && <span className="conversations__badge">{state.unreadByTab[tab.value]}</span>}
              </button>
            ))}
          </div>

          <div className="conversations__filters">
            <Button
              icon={<FilterOutlined />}
              type={isFiltersOpen ? 'primary' : 'default'}
              ghost={isFiltersOpen}
              aria-expanded={isFiltersOpen}
              onClick={() => setIsFiltersOpen((open) => !open)}
            >
              Filtres
              {state.activeFiltersCount > 0 && <span className="conversations__filters-dot" aria-label="Filtres actifs" />}
            </Button>
            <Select
              className="conversations__sort"
              classNames={{ popup: { root: 'conversations__sort-popup' } }}
              popupMatchSelectWidth={false}
              menuItemSelectedIcon={<CheckOutlined />}
              value={filters.sortBy}
              options={CONVERSATIONS_SORT_OPTIONS}
              onChange={actions.setSortBy}
            />
          </div>

          {isFiltersOpen && (
            <div className="conversations__filters-panel">
              {state.canSelectSubscription && (
                <div className="conversations__filters-row">
                  <TeamOutlined className="conversations__filters-label" aria-label="Ligne" />
                  <Select
                    className="conversations__filters-select"
                    showSearch
                    optionFilterProp="label"
                    value={filters.subscriptionId ?? ''}
                    options={state.subscriptionOptions}
                    onChange={actions.setSubscriptionId}
                  />
                </div>
              )}
              <div className="conversations__filters-row">
                <span className="conversations__filters-label">État</span>
                <Select
                  className="conversations__filters-select"
                  value={filters.readState}
                  options={CONVERSATIONS_READ_STATE_OPTIONS}
                  onChange={actions.setReadState}
                />
              </div>
              <div className="conversations__filters-row">
                <span className="conversations__filters-label">Période</span>
                <Select
                  className="conversations__filters-select"
                  value={filters.period}
                  options={CONVERSATIONS_PERIOD_OPTIONS}
                  onChange={actions.setPeriod}
                />
              </div>
            </div>
          )}

          {/* Reminds the active filters, even with the panel closed */}
          {state.activeFiltersCount > 0 && (
            <div className="conversations__filters-summary">
              <span>{state.filtersSummary}</span>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={actions.resetFilters}
                aria-label="Réinitialiser les filtres"
              />
            </div>
          )}

          <ConversationList
            conversations={state.conversations}
            selectedKey={selectedConversation?.key ?? null}
            isLoading={state.isLoading}
            isLoadingMore={state.isLoadingMore}
            hasMore={state.hasMore}
            onSelect={actions.selectConversation}
            onLoadMore={actions.loadMore}
          />
        </div>

        <div className="conversations__panel">
          {selectedConversation ? (
            <>
              <ConversationThread
                key={selectedConversation.key}
                conversation={selectedConversation}
                isContactOpen={isContactOpen}
                onToggleContact={() => setIsContactOpen((value) => !value)}
                onOpenContact={() => setIsDetailsOpen(true)}
                onSetRead={(read) => actions.setRead(selectedConversation, read)}
              />
              {isContactOpen && (
                <ConversationContactPanel
                  conversation={selectedConversation}
                  onOpenContact={() => setIsDetailsOpen(true)}
                  onClose={() => setIsContactOpen(false)}
                />
              )}
            </>
          ) : (
            <Empty
              className="conversations__placeholder"
              image={<MessageOutlined className="conversations__placeholder-icon" />}
              description="Sélectionnez une conversation"
            />
          )}
        </div>
      </div>

      <ContactDrawer
        contact={isDetailsOpen ? (selectedConversation?.contact ?? null) : null}
        onClose={() => setIsDetailsOpen(false)}
        canOpenConversation={false}
      />
    </PageScaffold>
  );
};

export default ConversationsPage;
